import { FC, useEffect, useState } from 'react';
import { useLocation, Link, useHistory } from 'react-router-dom';

import cn from 'clsx';
import Logo from '@images/cc.png';
import AccountIcon from '@images/bottomNav/individual/Account.png';
import NotificationIcon from '@images/bottomNav/individual/Notification.png';
import RecentIcon from '@images/bottomNav/individual/Recent.png';
import FeedsIcon from '@images/bottomNav/individual/Feeds.png';
import LogoWhite from '@images/logo-white.png';
import { useAuth } from '@hooks/Auth';
import { CompanyRole, Tier } from '@sdk/API';
import { DashboardIcon } from '@components/elements/Icons';
import useSwr from 'swr';
import { adminGetDefaultConfiguration } from '@sdk/managers/AdminManager';
import { getImageUrl } from '@utils/image';
import { GraphQLResult } from '@aws-amplify/api';
import NewIcon from '@images/scroll.png';
import Home from '@images/house-blank-duotone.svg';
import { words } from 'lodash';
const ActionIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path d="M4.1029 4L6.32103 6.66959H3.65783V13.795H6.32103V16.4646H1V4H4.1029Z" />
    <path d="M14.8716 4L11.651 7.89688L14.8573 11.7902H11.4048L9.92427 9.99851L8.44372 11.7902H4.99122L8.19311 7.89688L4.97784 4H8.43481L9.91892 5.8051L11.4048 4H14.8716Z" />
    <path d="M18.8494 16.4476H15.685L13.5284 16.4646V13.795H16.1862V6.66959H13.5284L15.7465 4H18.8494V16.4476Z" />
  </svg>
);

export type BottomNavbarItem = {
  active?: boolean;
  disabled?: boolean;
  icon?: string | JSX.Element;
  label: string | JSX.Element;
  value?: string;
  path?: string;
  unstyledIcon?: boolean;
};

type Props = {
  active?: string;
  items?: BottomNavbarItem[];
};

const NavbarItem: FC<BottomNavbarItem> = ({
  active,
  icon,
  label,
  path,
  disabled,
  unstyledIcon,
}) => {
  const rootCn = cn(
    'group w-full h-full px-6 inline-block text-center py-1 cursor-pointer font-bold flex space-x-2',
    {
      'opacity-60 cursor-not-allowed': disabled,
      'text-cyan': active,
      'hover:text-gray-300': !active && !disabled,
    }
  );

  const iconCn = cn('flex justify-center mr-1 h-6 w-6', {
    'opacity-80 group-hover:opacity-90': !disabled,
    'opacity-100': active,
  });

  const labelCn = cn('font-display text-xl');

  const content = (
    <>
      {unstyledIcon ? icon : <div className={iconCn}>{icon}</div>}
      {typeof label === 'string' ? (
        <div className={labelCn}>{label}</div>
      ) : (
        label
      )}
    </>
  );

  if (!path || disabled)
    return (
      <div role="button" className={rootCn}>
        {content}
      </div>
    );

  return (
    <Link to={path} className={rootCn} style={{ height: '1.8rem' }}>
      {content}
    </Link>
  );
};

const NAV_ITEMS = [
  {
    label: 'Home',
    value: 'home',
    path: '/home',
    icon: <img src={Home} alt="new" />,
  },
  {
    label: 'Action',
    value: 'action',
    icon: <ActionIcon />,
    path: '/action',
  },
  {
    label: 'Tertiary Projects',
    value: 'idea',
    path: '/idealist',
    icon: <img src={NewIcon} alt="new" style={{ filter: 'grayscale(100%)' }} />,
  },
  {
    label: 'Feeds',
    value: 'feeds',
    icon: <img src={FeedsIcon} alt="feeds" />,
    path: '/feed',
  },
  {
    label: 'Recent',
    value: 'recent',
    icon: <img src={RecentIcon} alt="recent" />,
    path: '/recent',
  },
  {
    label: 'Alerts',
    value: 'notifications',
    icon: <img src={NotificationIcon} alt="notifications" />,
    path: '/alerts',
  },
  {
    label: 'Account',
    value: 'account',
    icon: <img src={AccountIcon} alt="account" />,
    path: '/account',
  },
];

const COMPANY_NAV_OPTIONS = [
  {
    label: 'Home',
    value: 'home',
    path: '/home',
    icon: <img src={Home} alt="new" />,
  },
  {
    label: 'Actions',
    value: 'action',
    icon: <ActionIcon />,
    path: '/admin/actions',
  },
  {
    label: 'Dashboard',
    value: 'dashboard',
    icon: <DashboardIcon />,
    path: '/admin/dashboard',
  },
  {
    label: 'Tertiary Projects',
    value: 'idea',
    path: '/idealist',
    icon: <img src={NewIcon} alt="new" style={{ filter: 'grayscale(100%)' }} />,
  },
  {
    label: 'Feeds',
    value: 'feeds',
    icon: <img src={FeedsIcon} alt="feeds" />,
    path: '/feed',
  },
  {
    label: 'Recent',
    value: 'recent',
    icon: <img src={RecentIcon} alt="recent" />,
    path: '/recent',
  },
  {
    label: 'Alerts',
    value: 'notifications',
    icon: <img src={NotificationIcon} alt="notifications" />,
    path: '/alerts',
  },
  {
    label: 'Account',
    value: 'account',
    icon: <img src={AccountIcon} alt="account" />,
    path: '/account',
  },
];

const COMPANY_NAV_OPTIONS_NO_SETUP = [
  {
    label: 'Home',
    value: 'home',
    path: '/home',
    icon: <img src={Home} alt="new" />,
  },
  {
    label: 'Actions',
    value: 'action',
    icon: <ActionIcon />,
    path: '/admin/actions',
  },
  {
    label: 'Tertiary Projects',
    value: 'idea',
    path: '/idealist',
    icon: <img src={NewIcon} alt="new" style={{ filter: 'grayscale(100%)' }} />,
  },
  {
    label: 'Feeds',
    value: 'feeds',
    icon: <img src={FeedsIcon} alt="feeds" />,
    path: '/feed',
  },
  {
    label: 'Recent',
    value: 'recent',
    icon: <img src={RecentIcon} alt="recent" />,
    path: '/recent',
  },
  {
    label: 'Alerts',
    value: 'notifications',
    icon: <img src={NotificationIcon} alt="notifications" />,
    path: '/alerts',
  },
  {
    label: 'Account',
    value: 'account',
    icon: <img src={AccountIcon} alt="account" />,
    path: '/account',
  },
];

const Navbar: FC<Props> = ({ active }) => {
  const { dbUser, company } = useAuth();
  const location = useLocation();
  const history = useHistory();
  const [selected, setSelected] = useState<string>();
  const { data } = useSwr<any>(
    ['/site'],
    async (_query: string) => {
      const res = (await adminGetDefaultConfiguration()) as GraphQLResult<{
        result: {
          items: any[];
        };
      }>;
      const config = res.data?.result.items?.[0];
      console.log('config', config);
      if (!config) return {};
      const payload = {
        ...config,
        logoSrc: config?.logo ? await getImageUrl(config.logo) : undefined,
        pollImageSrc: config?.pollImage
          ? await getImageUrl(config.pollImage)
          : undefined,
      };
      return payload;
    },
    {
      revalidateOnFocus: false,
      onError: (err, key) => {
        console.log('Failed to fetch site', { key }, err);
      },
    }
  );

  useEffect(() => {
    if (selected === undefined) setSelected(active);
  }, [active, selected]);

  useEffect(() => {
    if (location.pathname.startsWith('/recent')) setSelected('recent');
    else if (location.pathname.startsWith('/feed')) setSelected('feeds');
    else if (location.pathname.startsWith('/alerts'))
      setSelected('notifications');
    else if (location.pathname.startsWith('/account')) setSelected('account');
    else if (location.pathname.startsWith('/action')) setSelected('action');
    else if (location.pathname.startsWith('/admin/actions'))
      setSelected('action');
    else if (location.pathname.startsWith('/admin/dashboard'))
      setSelected('dashboard');
    else if (location.pathname.startsWith('/home')) setSelected('home');
    else if (location.pathname.startsWith('/idealist')) setSelected('idea');
  }, [location.pathname, setSelected]);

  let options =
    company &&
    (company.role === CompanyRole.ADMIN ||
      company.role === CompanyRole.REPRESENTATIVE)
      ? COMPANY_NAV_OPTIONS
      : NAV_ITEMS;
  if (dbUser && dbUser.tier === Tier.ENTERPRISE && !company)
    options = COMPANY_NAV_OPTIONS_NO_SETUP;

  return (
    <nav
      style={{ minWidth: 270 }}
      className={cn(
        'flex flex-col justify-between items-center w-48 2xl:w-64 text-gray-600 bg-black'
      )}
    >
      <img
        className="object-contain w-auto h-4 m-6 cursor-pointer"
        src={Logo}
        alt="MegaX World"
        onClick={() => {
          history.push('/home');
        }}
      />
      <div className="flex items-start h-full mt-6">
        <div className="flex flex-col w-full space-y-2">
          {options.map((item) => (
            <NavbarItem
              key={`navbar-item-${item.value}`}
              active={selected === item.value}
              {...item}
            />
          ))}
        </div>
      </div>
      {/* <div className="my-6">
        {BOTTOM_ITEMS.map((item) => (
          <NavbarItem
            key={`navbar-item-${item.value}`}
            active={selected === item.value}
            {...item}
          />
        ))}
      </div> */}
    </nav>
  );
};

export default Navbar;
